import { Box, Typography } from "@mui/material";
import type { JSX } from "react";

import { LOGO_URL } from "../../../lib/crmApi.js";

/**
 * Whose portal this is — the first thing on the first screen.
 *
 * THE FORM IS A LETTERHEAD, deliberately, and not a dashboard hero: the
 * landing page opens with the company's own nameplate — the mark, and the
 * name.
 * A reseller-of-reseller sees a SUBTREE, which makes "whose data am I looking
 * at" a live question here in a way it never is in a single-tenant app; a
 * masthead answers it before anyone reads a number.
 *
 * It carried a strapline and a "city · currency" line under the name until
 * both were cut — they restated what the rail and Settings already say, and
 * a masthead that has to explain itself is not a masthead.
 *
 * The name comes from `issuerDisplayName` (legal name if they have set one,
 * else the name XOP knows them by).
 */

/** Initials for the fallback plate: at most two, from the first two words
 * that start with a letter or digit. "Acme Resell BV" → "AR". */
export function monogram(name: string): string {
  const words = name
    .split(/[\s.,\-—]+/)
    .map((w) => w.trim())
    .filter((w) => /^[\p{L}\p{N}]/u.test(w));
  const letters = words.slice(0, 2).map((w) => w[0]!.toUpperCase());
  return letters.join("") || "?";
}

function LogoPlate({ name, hasLogo }: { name: string; hasLogo: boolean }): JSX.Element {
  return (
    <Box
      sx={{
        flexShrink: 0,
        display: "grid",
        placeItems: "center",
        width: { xs: 56, sm: 68 },
        height: { xs: 56, sm: 68 },
        borderRadius: 1.5,
        overflow: "hidden",
        bgcolor: hasLogo ? "background.paper" : (t) => t.palette.accent.soft,
        border: 1,
        borderColor: hasLogo ? "divider" : "transparent",
        p: hasLogo ? 0.75 : 0,
      }}
    >
      {hasLogo ? (
        <Box
          component="img"
          src={LOGO_URL}
          alt={name}
          sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
        />
      ) : (
        <Typography
          aria-hidden
          sx={{
            fontFamily: (t) => t.typography.h1.fontFamily,
            fontSize: { xs: 20, sm: 24 },
            fontWeight: 600,
            letterSpacing: "0.02em",
            color: (t) => t.palette.accent[700],
          }}
        >
          {monogram(name)}
        </Typography>
      )}
    </Box>
  );
}

export function Nameplate({ name, hasLogo }: { name: string; hasLogo: boolean }): JSX.Element {
  return (
    // Centred, not top-aligned: with the strapline and the trading details
    // gone the text block is two lines, and hanging it from the top of a 68px
    // plate left the mark floating above the name it belongs to.
    <Box sx={{ display: "flex", gap: { xs: 2, sm: 2.5 }, alignItems: "center" }}>
      <LogoPlate name={name} hasLogo={hasLogo} />

      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="p"
          sx={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: (t) => t.palette.accent[700],
            mb: 0.5,
          }}
        >
          Reseller portal
        </Typography>

        <Typography
          component="h1"
          sx={{
            fontFamily: (t) => t.typography.h1.fontFamily,
            fontWeight: 600,
            // Fluid rather than stepped: the rail eats 288px, so the name has
            // very different room at 1280 and at 1920.
            fontSize: "clamp(1.55rem, 1.05rem + 1.7vw, 2.3rem)",
            lineHeight: 1.12,
            letterSpacing: "-0.02em",
            color: "text.primary",
            overflowWrap: "anywhere",
          }}
        >
          {name}
        </Typography>

      </Box>
    </Box>
  );
}
